<script setup>
import { cloneDeep, flatten, isEmpty, isEqual, keyBy, orderBy, uniqBy } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import HawkTreeSelect from '~/common/components/organisms/hawk-tree/hawk-treeselect.vue';
import { useProjectManagementStore } from '~/project-management/store/pm.store';

const props = defineProps({
  fieldUids: {
    type: Object,
    required: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'update']);

const $t = inject('$t');
const $services = inject('$services');

const project_management_store = useProjectManagementStore();
const { active_task, active_task_auto_progress_cache } = storeToRefs(project_management_store);

const route = useRoute();

const vueform_columns = {
  default: {
    container: 12,
    label: 3,
    wrapper: 9,
  },
  sm: {
    label: 4,
  },
  md: {
    label: 4,
  },
  lg: {
    label: 4,
  },
};

const state = reactive({
  fields: {
    list: [],
  },
  workflows: [],
  all_fields: [],
  name_map: {},
  terra_weights_map: {},
  selected_fields: {
    data: [],
  },
  reload_count: 0,
  tree_uid_to_field_uid: {},
  field_uid_to_tree_uids: {},
  prev_reconciled_tree_uids: [],
});

const hawk_tree_ref = ref(null);

const field_uids = computed(() => props.fieldUids);

// Same field uid can appear under multiple workflows (Terra data shape). HawkTreeSelect requires unique node identity, so we tag each field with a per-workflow tree_uid and translate at the boundary.
function buildTreeUidMaps() {
  state.tree_uid_to_field_uid = {};
  state.field_uid_to_tree_uids = {};
  for (const wf of state.workflows || []) {
    for (const field of wf.fields || []) {
      if (!field.tree_uid)
        continue;
      state.tree_uid_to_field_uid[field.tree_uid] = field.uid;
      if (!state.field_uid_to_tree_uids[field.uid])
        state.field_uid_to_tree_uids[field.uid] = [];
      state.field_uid_to_tree_uids[field.uid].push(field.tree_uid);
    }
  }
}

function normalizeToTreeUids(values) {
  const result = [];
  for (const v of values || []) {
    if (state.field_uid_to_tree_uids[v])
      result.push(...state.field_uid_to_tree_uids[v]);
    else if (state.tree_uid_to_field_uid[v])
      result.push(v);
  }
  return [...new Set(result)];
}

function treeUidsToFieldUids(tree_uids) {
  const set = new Set();
  for (const t of tree_uids || [])
    set.add(state.tree_uid_to_field_uid[t] ?? t);
  return [...set];
}

function reconcileLinkedSelection(emitted_tree_uids) {
  const prev_set = new Set(state.prev_reconciled_tree_uids || []);
  const new_set = new Set(emitted_tree_uids || []);

  const fids = new Set();
  for (const t of [...prev_set, ...new_set]) {
    const fid = state.tree_uid_to_field_uid[t];
    if (fid)
      fids.add(fid);
  }

  const result = new Set();
  for (const fid of fids) {
    const instances = state.field_uid_to_tree_uids[fid] || [];
    const prev_count = instances.filter(t => prev_set.has(t)).length;
    const new_count = instances.filter(t => new_set.has(t)).length;
    const was_fully = prev_count === instances.length && instances.length > 0;
    if (was_fully && new_count < prev_count)
      continue;
    if (new_count > 0)
      instances.forEach(t => result.add(t));
  }
  return [...result];
}

function onTreeFormUpdate(emitted_tree_uids) {
  const reconciled = reconcileLinkedSelection(emitted_tree_uids);
  const reconciled_set = new Set(reconciled);
  const emitted_set = new Set(emitted_tree_uids || []);
  const to_check = reconciled.filter(t => !emitted_set.has(t));
  const to_uncheck = (emitted_tree_uids || []).filter(t => !reconciled_set.has(t));

  state.selected_fields.data = reconciled;
  state.prev_reconciled_tree_uids = [...reconciled];
  updateRows(treeUidsToFieldUids(reconciled));
  emitUpdates();

  [...to_check, ...to_uncheck].forEach((t) => {
    hawk_tree_ref.value?.handleStateChange?.(t);
  });
}

onMounted(() => {
  if (active_task_auto_progress_cache.value) {
    state.workflows = active_task_auto_progress_cache.value.workflows;
    state.all_fields = active_task_auto_progress_cache.value.all_fields;
    state.name_map = active_task_auto_progress_cache.value.name_map;
    state.terra_weights_map = active_task_auto_progress_cache.value.terra_weights_map;
    buildTreeUidMaps();

    const field_uids = Object.keys(props.fieldUids ?? {});
    const cached_selected = active_task_auto_progress_cache.value?.selected_fields?.data ?? field_uids;
    state.selected_fields.data = normalizeToTreeUids(cached_selected);
    state.prev_reconciled_tree_uids = [...state.selected_fields.data];
    state.reload_count++;
    state.fields.list = [];
    const seen_uids = new Set();
    for (const field of state.all_fields) {
      if (field_uids.includes(field.uid) && !seen_uids.has(field.uid)) {
        seen_uids.add(field.uid);
        state.fields.list.push({
          name: field.uid,
          label: field.name,
          weights: Math.round(props.fieldUids[field.uid] * 100),
        });
      }
    }
  }
});

watch(field_uids, (value) => {
  const field_uids = Object.keys(value ?? {});
  state.selected_fields.data = normalizeToTreeUids(field_uids);
  state.prev_reconciled_tree_uids = [...state.selected_fields.data];
  state.reload_count++;
  project_management_store.auto_update_progress_cache[active_task.value.uid] = {
    ...active_task_auto_progress_cache.value,
    selected_fields: cloneDeep(state.selected_fields),
  };
  state.fields.list = [];
  const seen_uids = new Set();
  for (const field of state.all_fields) {
    if (field_uids.includes(field.uid) && !seen_uids.has(field.uid)) {
      seen_uids.add(field.uid);
      state.fields.list.push({
        name: field.uid,
        label: field.name,
        weights: Math.round(value[field.uid] * 100),
      });
    }
  }
});

watch(() => state.selected_fields.data, (newVal, oldVal) => {
  if (!isEqual(newVal, oldVal) && newVal.length < oldVal.length) {
    updateRows(treeUidsToFieldUids(newVal));
    emitUpdates();
  }
});

async function getWorkflows() {
  const { data } = await $services.terra_workflow.getAll({
    query: {
      asset: route.params.asset_id,
    },
  });

  state.name_map = {};
  state.terra_weights_map = {};

  data.forEach((workflow) => {
    Object.keys(workflow.data).forEach((data_key) => {
      state.terra_weights_map = { ...state.terra_weights_map, ...workflow.data[data_key].weights };
    });
  });

  state.workflows = orderBy(data, wf => wf.name.toLowerCase(), ['asc']).map((wf) => {
    const child = uniqBy(
      flatten(
        Object.entries(wf.data || {})
          .sort((a, b) => a[1].order_index - b[1].order_index)
          .map(([_, item]) => item.fields),
      ),
      f => f.uid,
    ).map(f => ({ ...f, tree_uid: `${wf.uid}::${f.uid}` }));
    state.name_map[wf.uid] = wf.name;
    return {
      name: wf.name,
      uid: wf.uid,
      tree_uid: wf.uid,
      fields: child,
    };
  });

  state.all_fields = state.workflows.map(flow => flow.fields).flat();
  state.all_fields.forEach((field) => {
    state.name_map[field.uid] = field.name;
  });
  buildTreeUidMaps();

  project_management_store.auto_update_progress_cache[active_task.value.uid] = {
    ...active_task_auto_progress_cache.value,
    workflows: state.workflows,
    all_fields: state.all_fields,
    name_map: state.name_map,
    terra_weights_map: state.terra_weights_map,
  };
}

function updateRows(new_selected_fields) {
  // remove old filters
  let rows = state.fields.list.filter(f => new_selected_fields.includes(f.name));

  const existing_names = rows.map(f => f.name);

  // add new fields
  new_selected_fields.forEach((new_field) => {
    if (!existing_names.includes(new_field)) {
      rows.push({
        name: new_field,
        label: state.name_map[new_field],
        weights: null,
      });
    }
  });

  rows = rows.map((row) => {
    let weights = row.weights;
    const field_weight_map = keyBy(active_task_auto_progress_cache.value?.activity_weights_data || [], 'field_uid');
    if (field_weight_map[row.name]) {
      weights = field_weight_map[row.name]?.weight;
    }
    else if (!Number.isNaN(state.terra_weights_map[row.name]) && !existing_names.includes(row.name)) {
      weights = state.terra_weights_map[row.name] * 100;
    }
    return {
      ...row,
      weights,
    };
  });

  state.fields.list = [...rows];
}

function emitUpdates() {
  const cpy = [...state.fields.list];
  emit('update', cpy);
}

if (isEmpty(active_task_auto_progress_cache.value))
  getWorkflows();

function updateFieldsCache() {
  project_management_store.auto_update_progress_cache[active_task.value.uid] = cloneDeep({
    ...active_task_auto_progress_cache.value,
    workflows: state.workflows,
    all_fields: state.all_fields,
    name_map: state.name_map,
    terra_weights_map: state.terra_weights_map,
    selected_fields: state.selected_fields,
  });
}

defineExpose({
  updateFieldsCache,
});
</script>

<template>
  <Vueform
    v-model="state.selected_fields"
    size="sm"
    sync
    :columns="vueform_columns"
    :display-errors="false"
  >
    <HawkTreeSelect
      :key="state.workflows.length + state.reload_count"
      ref="hawk_tree_ref"
      :options="{
        name: 'data',
        appendTo: '#pm-update-auto-progress-modal',
        placeholder: $t('Choose one/more construction activities'),
        description: $t('Select atleast one construction activity to automatically sync the completion progress'),
        loading: props.isLoading,
      }"
      :data="state.workflows"
      children_key="fields"
      :label="$t('Activities')"
      label_key="name"
      value_key="tree_uid"
      select_type="LEAF_PRIORITY"
      :initial_state="state.selected_fields.data"
      :rules="['required']"
      :messages="{ required: 'This field is required.' }"
      @update-form="onTreeFormUpdate($event)"
    />
  </Vueform>
</template>
